/**
 * ShareCardTemplate Component
 *
 * Base template for all shareable card formats (Narrator, Expert, Combined).
 * Provides fixed Instagram Stories dimensions (1080x1920px) with safe zones
 * and common layout elements (app branding, watermark).
 *
 * ## Design Specifications
 * - **Total Dimensions**: 1080w × 1920h (9:16 aspect ratio)
 * - **Safe Zone**: 1080w × 1610h (avoid UI overlap from Instagram Stories UI)
 * - **Top Padding**: 155px (Instagram top bar)
 * - **Bottom Padding**: 155px (Instagram bottom bar)
 *
 * ## Layout Structure
 * ```
 * ┌─────────────────┐ 0px
 * │  Top Padding    │ 155px (Instagram UI)
 * ├─────────────────┤
 * │                 │
 * │  Content Area   │ 1610px (safe zone)
 * │  (Provided by   │
 * │   child cards)  │
 * │                 │
 * ├─────────────────┤ 1765px
 * │ Bottom Padding  │ 155px (Instagram UI)
 * └─────────────────┘ 1920px
 * ```
 *
 * ## Usage
 * ```tsx
 * <ShareCardTemplate backgroundColor="#F5F3FF">
 *   <NarratorShareCard analysis={analysis} context={context} />
 * </ShareCardTemplate>
 * ```
 *
 * Created: 2025-01-11 (Task 2.2: Viral Share Formats)
 */

import type React from "react";
import { View, Text, StyleSheet } from "react-native";
import { useTranslation } from "@dramadetector/shared";
import { SHARE_FORMAT_DIMENSIONS } from "@dramadetector/shared/og";
import type { ShareFormat } from "@dramadetector/shared/og";

/**
 * Props for ShareCardTemplate component
 */
export interface ShareCardTemplateProps {
	/**
	 * Background color for the entire card
	 * Examples: "#F5F3FF" (purple for Narrator), "#EFF6FF" (blue for Expert)
	 */
	backgroundColor: string;

	/**
	 * Output format for the card. Determines dimensions and safe zones.
	 * @default 'reel'
	 */
	format?: ShareFormat;

	/**
	 * Child components containing card-specific content
	 * (NarratorShareCard, ExpertShareCard, CombinedShareCard)
	 */
	children: React.ReactNode;

	/**
	 * Optional test ID for E2E testing
	 * @default 'share-card-template'
	 */
	testID?: string;
}

/**
 * Instagram Stories dimensions and safe zones
 * Reference: https://www.socialmediatoday.com/news/instagram-stories-dimensions/
 */
export const INSTAGRAM_STORIES_DIMENSIONS = {
	/** Total width (9:16 aspect ratio) */
	WIDTH: 1080,
	/** Total height (9:16 aspect ratio) */
	HEIGHT: 1920,
	/** Safe zone height (avoids Instagram UI overlap) */
	SAFE_HEIGHT: 1610,
	/** Top padding (Instagram top bar) */
	TOP_PADDING: 155,
	/** Bottom padding (Instagram bottom bar) */
	BOTTOM_PADDING: 155,
} as const;

export function ShareCardTemplate({
	backgroundColor,
	format = "reel",
	children,
	testID = "share-card-template",
}: ShareCardTemplateProps) {
	const { t } = useTranslation("share", { useSuspense: false }) as {
		t: (key: string, options?: any) => string;
	};
	const dims = SHARE_FORMAT_DIMENSIONS[format];

	return (
		<View
			style={[
				styles.container,
				{
					backgroundColor,
					width: dims.width,
					height: dims.height,
				},
			]}
			testID={testID}
		>
			{/* Top Safe Zone Padding */}
			<View
				style={{ height: dims.topPadding }}
				testID={`${testID}-top-padding`}
			/>

			{/* Content Area (Safe Zone) */}
			<View
				style={[styles.contentArea, { height: dims.safeHeight }]}
				testID={`${testID}-content`}
			>
				{children}
			</View>

			{/* Bottom Safe Zone Padding */}
			<View
				style={{ height: dims.bottomPadding }}
				testID={`${testID}-bottom-padding`}
			/>

			{/* App Watermark */}
			<View style={styles.watermark} testID={`${testID}-watermark`}>
				<Text style={styles.watermarkText}>{t("share_card.watermark")}</Text>
			</View>
		</View>
	);
}

const styles = StyleSheet.create({
	container: {
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "flex-start",
		position: "relative",
	},
	contentArea: {
		width: "100%",
		paddingHorizontal: 48, // 48px horizontal padding
		flexDirection: "column",
		alignItems: "stretch",
		justifyContent: "center",
	},
	watermark: {
		position: "absolute",
		bottom: 180, // Above Instagram bottom bar (155px + 25px margin)
		right: 48,
		backgroundColor: "rgba(0, 0, 0, 0.5)",
		paddingHorizontal: 16,
		paddingVertical: 8,
		borderRadius: 20,
	},
	watermarkText: {
		fontSize: 14,
		fontWeight: "600",
		color: "#FFFFFF",
		fontFamily: "Inter", // Use Inter for brand consistency
	},
});
